import angular from 'angular';
import limitedReceiptItemsDialogTemplate from './LimitedReceiptItemsDialog.html';
import CSMCommonWeb from 'csm-common-web';
import booksService from '../../booksServices';

export default angular
  .module('csm.books.com.LimitedReceiptItemsDialog', [
    CSMCommonWeb,
    'ui.bootstrap',
    booksService
  ])
  .service('LimitedReceiptItemsDialog', DialogControllerService)
  .component('limitedReceiptItems', limitedReceiptItemsComponent()).name;

/*******************************************************************************
 * DialogControllerService */
/**
 * This is the controller for the dialog service. I set this up like this because
 * of the amount of logic that we have to do BEFORE we know if we can show the
 * modal. Since I had to do so much logic I decided to setup a controller below
 * here and then inject the functions so they could be separated into digestible
 * pieces.
 *******************************************************************************/
/* @ngInject */ function DialogControllerService(
  $uibModal,
  BooksServices,
  SERVER,
  BusyScreen,
  Alert,
  $injector
) {
  this.queryLimitRestrictions = $injector.invoke(queryLimitRestrictions, null, {
    BooksServices,
    SERVER,
    Alert,
    BusyScreen
  });
  this.isGroupItem = $injector.invoke(isGroupItem, null, { SERVER });
  this.mapLimitRestrictions = $injector.invoke(mapLimitRestrictions, null, {
    SERVER
  });
  this.flattenReceiptItems = $injector.invoke(flattenReceiptItems, null, {
    SERVER
  });

  // This has to be this.show because that is what $uibModal expects.
  this.show = $injector.invoke(showDialog, null, { $uibModal, BusyScreen });
}

/*******************************************************************************
 * showDialog */
/**
 * This is the actual start of the whole process of showing the modal. This
 * function is called from the parent component.
 *
 * @param  receiptItems  Receipt items that are item sales or item orders.
 * @param  paymentsByEntityID  Payments for the entity; entityID => paymentAmt.
 * @param  receiptDate  - The date of the receipt (important for limiters rules)
 *******************************************************************************/
/* @ngInject */ function showDialog($uibModal, BusyScreen, $q, Alert) {
  return function (receiptItems, paymentsByEntityID, receiptDate) {
    BusyScreen.setBusy(true);

    /**
     * Here I make a deep copy of the original array so that way the original array is not modified. This is important because I add
     * properties to the receipt items and I do not want to modify the original array.
     */
    const deepCopyReceiptsItems = angular.copy(receiptItems);

    return this.queryLimitRestrictions(deepCopyReceiptsItems, receiptDate)
      .then((receiptItemsWithSaleLimits) => {
        BusyScreen.setBusy(false);

        const receiptItemsWithRestrictions = this.mapLimitRestrictions(
          receiptItemsWithSaleLimits,
          paymentsByEntityID
        );

        const flattenedReceiptItems = this.flattenReceiptItems(
          receiptItemsWithRestrictions
        );

        /**
         * If there are no items that violate limiters then we do not show the dialog at all. Since we also filter out items in the
         * flattenReceiptItems function we can be sure that if there are no items in the flattenedReceiptItems array then there are no
         * items that violate limiters.
         */
        if (flattenedReceiptItems.length === 0) {
          return true;
        }

        return $uibModal.open({
          backdrop: 'static',
          component: 'limitedReceiptItems',
          /**
           * @param {array} flattenedReceiptItems - Flattened receipt items with limiter restrictions in a single array for the HTML to display.
           */
          resolve: {
            flattenedReceiptItems: () => flattenedReceiptItems
          },
          size: 'md'
        }).result;
      })
      .catch((error) => {
        /**
         * When the modal closes it will return a promise rejection with an error of undefined. So if the error is undefined
         * we do not log it or alert it. If the error is defined then show an error message to the user and log the error. This
         * is also here to catch the bubbled up error from the queryLimitRestrictions function.
         */
        if (error !== undefined) {
          console.error(error);
          Alert.error(
            `There was an issue retrieving the item limiters for your receipt! ${error}`,
            'Item Limiter Restrictions'
          );
        }

        /**
         * We return a promise rejection so the promise chain will stop and user will be returned to the default app screen before this modal was
         * called.
         */
        return $q.reject();
      });
  };
}

/*******************************************************************************
 * queryLimitRestrictions */
/**
 * Queries the checkLimitersRestrictions service once for all receipt items
 * (group items send their sub items instead of themselves) and attaches the sale
 * limits to the receipt items so they can be easily processed in later steps.
 * All items are sent in a single request as one flat, ordered `limiters` array,
 * and the server returns exactly one SaleLimits entry per limiter, in that same
 * order. We rely on that positional correspondence - not on itemID - to match
 * each result back to the item that requested it, since the same itemID can
 * legitimately appear more than once in one receipt (e.g. the same product
 * bought for two different entities, or twice for the same entity with different
 * options) and itemID alone can't tell those apart.
 *
 * @param  receiptItems  - An array of receipt item objects.
 * @param  receiptDate  - The date of the receipt.
 *******************************************************************************/
/* @ngInject */ function queryLimitRestrictions(BooksServices, BusyScreen, $q) {
  return function (receiptItems, receiptDate) {
    /**
     * `targets` is built in lockstep with `limiterItems` so `targets[i]` is always the receipt item
     * (or sub item) that `limiterItems[i]` was built from, and therefore the object that
     * `saleLimits[i]` belongs to once the server responds.
     */
    const targets = [];
    const limiterItems = receiptItems.reduce((items, receiptItem) => {
      if (this.isGroupItem(receiptItem)) {
        receiptItem.subItems.forEach((subItem) => {
          items.push(buildLimiterItem(subItem));
          targets.push(subItem);
        });
        return items;
      }

      items.push(buildLimiterItem(receiptItem));
      targets.push(receiptItem);
      return items;
    }, []);

    if (limiterItems.length === 0) return $q.resolve(receiptItems);

    return BooksServices.Receipts.checkLimitersRestrictions({
      limiters: limiterItems,
      fyDate: receiptDate
    })
      .then((saleLimits) => {
        targets.forEach((target, index) => {
          target.saleLimits = [saleLimits[index]];
        });

        return receiptItems;
      })
      .finally(() => BusyScreen.setBusy(false));
  };
}

/*******************************************************************************
 * buildLimiterItem */
/**
 * Takes on the receipt item and returns it in the specific format for the
 * limiter service.
 *******************************************************************************/
/* @ngInject */ function buildLimiterItem(receiptItem) {
  const optionIDs = receiptItem.options
    ? receiptItem.options.map(
        (ledgerItemOption) => ledgerItemOption.itemOptionID
      )
    : [];

  return {
    itemID: receiptItem.itemID,
    entityID: receiptItem.entityID,
    optionIDs
  };
}

/*******************************************************************************
 * createLimiterMessages */
/**
 * For each receipt item or sub item with sale limits we need to create all of
 * the limiter messages for the item.
 *******************************************************************************/
/* @ngInject */ function createLimiterMessages(
  receiptItemWithSaleLimits,
  currentPayments
) {
  const limiterMessages = [];

  /**
   * Since each receipt item can have multiple sale limits we need to check each one. Also each sale limit
   * can have multiple limiters. We need to check each limiter for each sale limit.
   */
  receiptItemWithSaleLimits.saleLimits.forEach((saleLimit) => {
    /**
     * Check if there is enough quantity to sell.
     *
     * We purposely check if onHandQty is undefined or null because it could be 0 and we still want to show the message.
     *
     * receiptItem.quantity = 3
     * saleLimit.onHandQty = 2
     */
    if (
      saleLimit.onHandQty != undefined &&
      saleLimit.onHandQty != null &&
      receiptItemWithSaleLimits.quantity > saleLimit.onHandQty
    ) {
      limiterMessages.push(
        `Quantity exceeds available amount of ${saleLimit.onHandQty}`
      );
    }

    if (
      !saleLimit.limiters ||
      !saleLimit.limiters.length ||
      saleLimit.limiters.length === 0
    ) {
      return;
    }

    saleLimit.limiters.forEach((limiter) => {
      /**
       * When checking the balance limiter make sure to factor in the current payments that are in the receipt. Otherwise
       * the balance limiter will always be violated even if they have made a payment within the same receipt.
       *
       * Also the (currentPayments || 0) is important just in case currentPayments is undefined. This will prevent the balance limit from
       * not being violated due to bad js math with undefined. Specifically when you add a number and undefined together you get NaN which is not
       * greater than the balance limit returning a false negative.
       */
      if (
        limiter.balanceLimit > 0 &&
        limiter.entityBalance - (currentPayments || 0) > limiter.balanceLimit
      )
        limiterMessages.push(
          `${receiptItemWithSaleLimits.name} has too much debt to purchase the item`
        );

      /**
       * Destiny is currently paused (a manual refresh is in flight) for this
       * entity's site or this entity specifically - the Destiny-fine portion
       * of entityBalance above may be stale or incomplete right now. This is
       * a warning, not a hard block - staff can still override it the same
       * way they override any other limiter message below.
       */
      if (limiter.balanceLimit > 0 && limiter.destinyPaused)
        limiterMessages.push(
          `${receiptItemWithSaleLimits.name}'s Destiny fine balance is currently refreshing and may not be up to date`
        );

      if (
        limiter.quantityLimit &&
        limiter.quantityLimit <
          limiter.quantity + receiptItemWithSaleLimits.quantity
      )
        limiterMessages.push(
          `The quantity limit of ${limiter.quantityLimit} has been reached`
        );

      if (limiter.gradeLimit)
        limiterMessages.push(`It is restricted to grade ${limiter.gradeLimit}`);

      if (
        limiter.status != null &&
        (limiter.status.required || limiter.status.restricted)
      ) {
        const required_restricted = limiter.status.required
          ? 'requires'
          : limiter.status.restricted
            ? 'is restricted because of'
            : '';

        limiterMessages.push(
          `It ${required_restricted} the ${limiter.status.description} tag.`
        );
      }
    });
  });

  return limiterMessages;
}

/**
 * @description This takes in the list of receipt items that have just had their limiter messages mapped to them and flattens the
 * items and sub items into a single array. This is done so that we can easily display the items in the limiter modal. The limiter modal
 * just displays them in index order so its important that the items are in the correct order. This also filters out any items that do not
 * have any limiters.
 *
 * @param {Array<Object>} receiptItemsWithRestrictions
 * @returns {Array<Object>} receipt items with limiter restrictions flattened
 */
/* @ngInject */ function flattenReceiptItems() {
  return function (receiptItemsWithRestrictions) {
    return receiptItemsWithRestrictions.reduce(
      (flattenedItems, receiptItem) => {
        /**
         * If there are no limiters then we do not show the item in the dialog at all. This works for group and non-group items because
         * groups get a limiter message if any of their sub items have limiters.
         */
        if (receiptItem.limiterMessages.length === 0) return flattenedItems;

        flattenedItems.push(receiptItem);

        if (this.isGroupItem(receiptItem)) {
          flattenedItems = flattenedItems.concat(
            receiptItem.subItems
              /**
               * If there are no limiters then we do not show the item in the dialog at all. In group items not all the items will always have
               * limiters so we only show the sub items that do.
               */
              .filter((subItem) => subItem.limiterMessages.length !== 0)
              .map((subItem) => Object.assign(subItem, { isSubItem: true }))
          );
        }

        return flattenedItems;
      },
      []
    );
  };
}

/**
 * @description Takes in the list of receipts items with sale limits and the mapped entity payments and returns
 * the receipt items with the limiter messages mapped to the receipt items. The purpose of this is to
 *
 * @param {Array<Object>} receiptItemsWithSaleLimits
 * @param {Object} paymentsByEntityID Hash map object { entityID: paymentAmt }
 *
 * @returns {Array<Object>} The receipt items with the limiter messages mapped to the receipt items.
 */
/* @ngInject */ function mapLimitRestrictions() {
  return function (receiptItemsWithSaleLimits, paymentsByEntityID) {
    /**
     * For all the sale limits we need to match which sale limits apply to which receipt items.
     */
    return receiptItemsWithSaleLimits.map((receiptItemWithSaleLimits) => {
      receiptItemWithSaleLimits.limiterMessages = [];

      const currentPayments = paymentsByEntityID.get(
        receiptItemWithSaleLimits.entityID
      );

      if (this.isGroupItem(receiptItemWithSaleLimits)) {
        receiptItemWithSaleLimits.subItems.map((subItem) =>
          Object.assign(subItem, {
            limiterMessages: createLimiterMessages(subItem, currentPayments)
          })
        );
      } else {
        receiptItemWithSaleLimits.limiterMessages = createLimiterMessages(
          receiptItemWithSaleLimits,
          currentPayments
        );
      }

      const isGroupItemWithLimits =
        this.isGroupItem(receiptItemWithSaleLimits) &&
        receiptItemWithSaleLimits.subItems.some(
          (item) => item.limiterMessages && item.limiterMessages.length > 0
        );

      /**
       * This adds a little blurb underneath the group item to make it clear that the problem(s) is/are the sub items. We rely on this in logic in later
       * function such as flattenReceiptItems so if this is removed please re-factor that logic.
       */
      if (isGroupItemWithLimits)
        receiptItemWithSaleLimits.limiterMessages = [
          'One or more sub items have limitations!'
        ];

      return receiptItemWithSaleLimits;
    });
  };
}

/************************************************* End Of Open Dialog Routines ********************************************************************/

/* @ngInject */ function limitedReceiptItemsComponent() {
  return {
    bindings: {
      resolve: '<',
      modalInstance: '<'
    },
    controller: limitedReceiptItemsController,
    template: limitedReceiptItemsDialogTemplate
  };
}

/* @ngInject */ function limitedReceiptItemsController($injector, $scope) {
  const $ctrl = this;

  const $svcs = { $ctrl, $scope };

  $ctrl.$onInit = function () {
    $injector.invoke(initCtrl, null, Object.assign($svcs, { $svcs }));
  };
}

/* @ngInject */ function initCtrl($ctrl, $injector, $svcs) {
  Object.assign($ctrl, {
    // Properties
    flattenedReceiptItems: $ctrl.resolve.flattenedReceiptItems,
    // Regular Functions
    getAdditionalDescription: getAdditionalDescription,
    isSubItem: isSubItem,
    // Injector Functions
    getItemDescription: $injector.invoke(getItemDescription, null, $svcs),
    isGroupItem: $injector.invoke(isGroupItem, null, $svcs)
  });
}

/**
 * @description Takes in the receipt item info and creates a string that combines who the item is for plus the class name if it exists.
 *
 * @param {Object} receiptItem
 * @returns {string} - EX: For: Baak, Ava Grace - Class 123
 */
/* @ngInject */ function getAdditionalDescription(receiptItem) {
  const descriptionName = receiptItem.name ? `\nFor: ${receiptItem.name}` : '';
  const descriptionClass =
    receiptItem.classID && receiptItem.className
      ? ` - ${receiptItem.className}`
      : '';

  return `${descriptionName}${descriptionClass}`;
}

/**
 * @description Looks through the receipt item info and creates a description string that gives a summary
 * of the item details.
 *
 * @returns {string} - Ex: GASB Kit For: Baak, Ava Grace - Class 123
 */
/* @ngInject */ function getItemDescription(SERVER, $ctrl) {
  return function (receiptItem) {
    if (receiptItem.type === SERVER.BooksDBConstants.LIT_SERVICE_FEE_CREDIT)
      return 'Convenience Fee';

    /*-----------------------------*/
    /* Build description from memo */
    /*-----------------------------*/
    const baseDescription = receiptItem.memo || '';
    const additionalInfo = !$ctrl.isSubItem(receiptItem)
      ? $ctrl.getAdditionalDescription(receiptItem)
      : '';

    const orderIndicator =
      receiptItem.type === SERVER.BooksDBConstants.LIT_ITEM_ORDER ? '*' : '';

    /*------------------------------------*/
    /* Return the constructed description */
    /*------------------------------------*/
    return baseDescription + additionalInfo + orderIndicator;
  };
}

/**
 * @description Checks if the receipt item is a sub item. This flag is created during the process of flattening the
 * receipt items array for the limiter modal display.
 *
 * @param {Object} receiptItem
 * @returns boolean - If true the receipt item is a sub item.
 */
/* @ngInject */ function isSubItem(receiptItem) {
  return receiptItem.isSubItem;
}

/**
 * @description
 *
 * @param {Object} receiptItem
 * @returns {boolean} - If true the item is a group item.
 */
/* @ngInject */ function isGroupItem(SERVER) {
  return function (receiptItem) {
    return receiptItem.subType === SERVER.BooksDBConstants.LIS_ITEM_GROUP;
  };
}
